import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { TooltipAlign, TooltipPosition } from 'cpool-components/general/Tooltip/types';
import type { AssignedUserSlot } from 'cpool-utils';
import { useTranslation } from 'react-i18next';

import { ActiveStar } from './ActiveStar';
import { TeamCategoryIcon } from './TeamCategoryIcon';
import { cn } from '~/utils/cnTailwind';

type Props = {
    canSetActive: boolean;
    disabled: boolean;
    name: string;
    onSetActive: () => void;
    slot: AssignedUserSlot;
};

export const AssignedUserLine: React.FC<Props> = ({ canSetActive, disabled, name, onSetActive, slot }) => {
    const { t } = useTranslation();
    const testId = `assigned-user-${slot.userId}`;

    if (slot.isActive || !canSetActive)
        return (
            <div className="flex items-center gap-1 min-w-0 text-sm text-midnight" data-testid={testId}>
                <TeamCategoryIcon category={slot.category} />
                <span className={cn('truncate', slot.isActive && 'font-medium')}>{name}</span>
                {slot.isActive && <ActiveStar className="text-midnight" />}
            </div>
        );

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    className="group flex items-center gap-1 min-w-0 text-sm text-midnight disabled:cursor-wait"
                    data-testid={testId}
                    disabled={disabled}
                    onClick={(evt) => {
                        evt.preventDefault();
                        evt.stopPropagation();
                        onSetActive();
                    }}
                    type="button"
                >
                    <TeamCategoryIcon category={slot.category} />
                    <span className="truncate group-hover:underline">{name}</span>
                    <ActiveStar className="invisible text-grey-hover group-hover:visible" />
                </button>
            </TooltipTrigger>
            <TooltipContent align={TooltipAlign.START} side={TooltipPosition.TOP}>
                {t('LEADS.ASSIGNED_USERS.SET_ACTIVE_TOOLTIP')}
            </TooltipContent>
        </Tooltip>
    );
};
